/**
 * Bundle 139 P0-2 - Feature flag admin toggle page.
 *
 * Lists every registered flag with its current state, source
 * (DB/env), last-flipped-by/at, and description. Each flip
 * requires typing CONFIRM verbatim in the confirmation modal
 * before Apply becomes enabled. Backend enforces the same guard.
 *
 * Guardrails: no em/en dashes; no eslint-disable; no photo/image
 * in HTML alt attributes.
 */
import React, { useCallback, useEffect, useState } from 'react';
import api from '@/utils/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { RefreshCw, Sparkles, Eye } from 'lucide-react';

const AdminFeatureFlagsPage = () => {
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState(null); // { flag, newValue }
  // Bundle 139 patch (Fix 4) - preview mode for the grandfathering
  // age-gate modal. Renders the actual AgeGateModal in a read-only
  // shell so ops can see the exact UX a real user will get before
  // flipping AGE_GATE_ENABLED on 480+ live users.
  const [previewOpen, setPreviewOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/settings/feature-flags');
      setFlags(data?.flags || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load feature flags');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="admin-feature-flags-page">
      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-secondary-800 mb-1">
            Settings * Bundle 139
          </div>
          <h1 className="font-display text-3xl font-bold text-primary-900 flex items-center gap-2">
            <Sparkles className="h-6 w-6" /> Feature flags
          </h1>
          <p className="text-neutral-600 text-sm mt-1 max-w-2xl">
            Every flag is DB-backed with an env-var fallback. Flips are audited. Some flips have blast-radius impact, shown in the confirmation modal.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="feature-flags-refresh-btn">
          <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      <div className="grid gap-3">
        {flags.map((f) => (
          <Card key={f.key} data-testid={`feature-flag-row-${f.key}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-semibold text-primary-900">{f.key}</span>
                    <span className={`text-[10px] uppercase tracking-widest rounded px-1.5 py-0.5 ${f.value ? 'bg-secondary-100 text-secondary-800' : 'bg-neutral-100 text-neutral-600'}`}
                             data-testid={`feature-flag-state-${f.key}`}>
                      {f.value ? 'ON' : 'OFF'}
                    </span>
                    <span className="text-[10px] uppercase tracking-widest text-neutral-500">
                      source: {f.source}
                    </span>
                  </div>
                  <p className="text-sm text-primary-700 mt-1">{f.description}</p>
                  <div className="text-[11px] text-neutral-500 mt-2 space-x-3">
                    <span>env fallback: {String(f.env_default)}</span>
                    {f.last_flipped_at ? (
                      <span>last flipped by {f.last_flipped_by || 'unknown'} at {new Date(f.last_flipped_at).toLocaleString()}</span>
                    ) : (
                      <span>never flipped</span>
                    )}
                    {f.impact?.would_see_modal_on_flip !== undefined ? (
                      <span className="text-amber-800 font-medium">
                        would prompt {f.impact.would_see_modal_on_flip} users on flip
                      </span>
                    ) : null}
                  </div>
                  {f.key === 'AGE_GATE_ENABLED' ? (
                    <div className="mt-3">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setPreviewOpen(true)}
                        data-testid="feature-flag-preview-age-gate-btn"
                      >
                        <Eye className="h-3.5 w-3.5 mr-1" /> Preview grandfathering modal (super_admin only)
                      </Button>
                    </div>
                  ) : null}
                </div>
                <Switch
                  checked={!!f.value}
                  onCheckedChange={(v) => setPending({ flag: f, newValue: v })}
                  data-testid={`feature-flag-switch-${f.key}`}
                />
              </div>
            </CardContent>
          </Card>
        ))}
        {flags.length === 0 && !loading ? (
          <Card>
            <CardContent className="p-6 text-neutral-500 text-sm" data-testid="feature-flags-empty">
              No feature flags registered.
            </CardContent>
          </Card>
        ) : null}
      </div>

      <FlipConfirmDialog
        pending={pending}
        onClose={() => setPending(null)}
        onDone={() => { setPending(null); load(); }}
      />

      {/* Bundle 139 patch Fix 4 - preview grandfathering modal. Wraps
          the real AgeGateModal in an admin preview shell that does NOT
          persist any submission to the super_admin's own record. */}
      {previewOpen ? (
        <AgeGatePreviewShell onClose={() => setPreviewOpen(false)} />
      ) : null}
    </div>
  );
};

const AgeGatePreviewShell = ({ onClose }) => {
  // AgeGateModal writes DOB via `POST /auth/age-verify`. Preview
  // mode must NEVER hit that endpoint against the current session.
  // We provide `onConfirmed` that only shows a toast and closes.
  const handleConfirmed = () => {
    toast.success('Preview complete. Nothing was saved.');
    onClose();
  };
  // Patch: the real modal writes via `api.post('/auth/age-verify')`.
  // For preview we intercept the network call by rendering the modal
  // inside an isolated fetch shim; simplest working approach here is
  // a two-part cover: (1) show the modal with `open=true`, (2) show a
  // "preview mode" badge on top. Submitting in preview will hit the
  // endpoint against the admin's own user, which the endpoint rejects
  // for over-18 admins (accepts and confirms) or persists as pending
  // for under-18 dates. To keep the UX honest and truly non-writing,
  // we render our OWN copy of the form fields here and skip the
  // real modal's submit path.
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="age-gate-preview-shell">
      <div className="w-full max-w-md rounded-xl bg-white shadow-xl p-5 space-y-4 relative">
        <div className="flex items-center gap-2">
          <span className="inline-block text-[10px] uppercase tracking-widest rounded px-2 py-0.5 bg-secondary-100 text-secondary-800 border border-secondary-200"
                    data-testid="age-gate-preview-badge">
            Preview mode * no changes will be saved
          </span>
        </div>
        <PreviewAgeGateForm onClose={onClose} onConfirmed={handleConfirmed} />
      </div>
    </div>
  );
};

const PreviewAgeGateForm = ({ onClose, onConfirmed }) => {
  const [dob, setDob] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState('');
  const [pending, setPending] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    if (!confirmed) { setError('Please confirm this is your accurate date of birth.'); return; }
    if (!dob || dob.length < 10) { setError('Enter your date of birth as YYYY-MM-DD.'); return; }
    // Client-side <18 check to match the server contract for the
    // preview. Purely visual; no writes.
    try {
      const parts = dob.split('-').map(Number);
      const now = new Date();
      const born = new Date(parts[0], parts[1] - 1, parts[2]);
      let age = now.getFullYear() - born.getFullYear();
      const m = now.getMonth() - born.getMonth();
      if (m < 0 || (m === 0 && now.getDate() < born.getDate())) age -= 1;
      if (age < 18) {
        setPending(true);
        return;
      }
      onConfirmed && onConfirmed();
    } catch (_) {
      setError('Could not parse the date. Try again.');
    }
  };

  return pending ? (
    <div data-testid="age-gate-preview-pending" className="space-y-3">
      <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
        <div className="font-semibold">Preview: this user would land in the pending review queue.</div>
        <div className="mt-1 text-xs">
          Copy the real user sees: "Our platform requires all users to be 18 or older. If you believe this is an error, contact support@dostination.com."
        </div>
      </div>
      <div className="flex justify-end">
        <Button variant="outline" onClick={onClose} data-testid="age-gate-preview-close-pending">Close</Button>
      </div>
    </div>
  ) : (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="text-lg font-display font-semibold text-primary-900">Confirm your date of birth</div>
      <p className="text-sm text-primary-700">
        Dostination is exclusively for adults 18 and older. To keep your account
        active, please confirm your date of birth.
      </p>
      <div className="space-y-1">
        <Label htmlFor="preview-dob">Date of birth</Label>
        <Input
          id="preview-dob"
          type="date"
          value={dob}
          onChange={(e) => setDob(e.target.value)}
          data-testid="age-gate-preview-dob-input"
        />
      </div>
      <label className="flex items-start gap-2 text-sm text-primary-700">
        <input type="checkbox" className="mt-1" checked={confirmed}
                   onChange={(e) => setConfirmed(e.target.checked)}
                   data-testid="age-gate-preview-confirm-checkbox" />
        <span>I confirm this is my accurate date of birth.</span>
      </label>
      {error ? (
        <div className="text-xs text-accent-800" data-testid="age-gate-preview-error">{error}</div>
      ) : null}
      <div className="flex justify-end gap-2">
        <Button variant="outline" type="button" onClick={onClose} data-testid="age-gate-preview-close">
          Close preview
        </Button>
        <Button type="submit" data-testid="age-gate-preview-submit">
          Continue (preview)
        </Button>
      </div>
    </form>
  );
};

const FlipConfirmDialog = ({ pending, onClose, onDone }) => {
  const [typed, setTyped] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (pending) { setTyped(''); setReason(''); } }, [pending]);
  if (!pending) return null;

  const { flag, newValue } = pending;
  const impact = flag?.impact?.would_see_modal_on_flip;
  const isAgeGateOn = flag.key === 'AGE_GATE_ENABLED' && newValue === true;
  const isAgeGateOff = flag.key === 'AGE_GATE_ENABLED' && newValue === false;

  let impactCopy;
  if (isAgeGateOn) {
    impactCopy = `This will show the age verification modal to ${impact ?? '?'} existing users on their next login. Type CONFIRM to proceed.`;
  } else if (isAgeGateOff) {
    impactCopy = 'This will disable the age gate for all users. New signups will not be blocked by age. Type CONFIRM to proceed.';
  } else {
    impactCopy = `Flip ${flag.key} to ${newValue ? 'ON' : 'OFF'}. Type CONFIRM to proceed.`;
  }

  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/admin/settings/feature-flags/${flag.key}`,
        { value: newValue, confirm: 'CONFIRM', reason: reason.trim() },
      );
      if (data?.ok) {
        toast.success(`${flag.key} = ${newValue ? 'ON' : 'OFF'}`);
        onDone && onDone();
      } else {
        toast.error('Flip failed');
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === 'string' ? d : d?.message;
      toast.error(msg || 'Flip failed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!pending} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid={`feature-flag-flip-dialog-${flag.key}`}>
        <DialogHeader>
          <DialogTitle>
            Flip {flag.key} to {newValue ? 'ON' : 'OFF'}
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-primary-800">{impactCopy}</p>
        <div className="space-y-1">
          <Label htmlFor="flip-confirm">Type CONFIRM</Label>
          <Input
            id="flip-confirm"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder="CONFIRM"
            autoComplete="off"
            data-testid={`feature-flag-confirm-input-${flag.key}`}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="flip-reason">Reason (optional)</Label>
          <Textarea
            id="flip-reason"
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid={`feature-flag-reason-${flag.key}`}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}
                      data-testid={`feature-flag-cancel-${flag.key}`}>
            Cancel
          </Button>
          <Button
            onClick={apply}
            disabled={busy || typed.trim() !== 'CONFIRM'}
            className="bg-primary-800 hover:bg-primary-900 text-white"
            data-testid={`feature-flag-apply-${flag.key}`}
          >
            {busy ? 'Applying...' : 'Apply'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdminFeatureFlagsPage;
